import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, useTheme, CircularProgress, Alert, Button } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../state/store';
import { verifyPaymentAndRegisterAsync } from '../../state/authentication/authenticationSlice';
import { CustomPaletteColor } from '../../theme';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useLoginPath } from '../../hooks/useLoginPath';

export const RegisterSuccess = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const loginPath = useLoginPath();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid session. No payment session found.');
      return;
    }

    dispatch(verifyPaymentAndRegisterAsync(sessionId))
      .unwrap()
      .then((result: any) => {
        setStatus('success');
        const data = result?.data || result;
        if (data?.existingUser) {
          setMessage('New company created and linked to your existing account. You can sign in now.');
        } else if (data?.paidActivation) {
          setMessage('Your account has been created and is now active. You can sign in immediately.');
        } else {
          setMessage('Your account has been set up successfully. You can sign in now.');
        }
      })
      .catch((err: any) => {
        if (typeof err === 'string' && err.includes('already completed')) {
          setStatus('success');
          setMessage('Your account has already been created. You can sign in now.');
        } else {
          setStatus('error');
          setMessage(typeof err === 'string' ? err : 'Something went wrong. Please contact support.');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      p: 2,
    }}>
      <Paper elevation={2} sx={{
        maxWidth: 480,
        width: '100%',
        p: 4,
        borderRadius: 3,
        textAlign: 'center',
        border: `1px solid ${(theme.palette.secondary as CustomPaletteColor)[200]}`,
      }}>
        {status === 'loading' && (
          <>
            <CircularProgress color="secondary" size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your payment...</Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              Please wait while we set up your account.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Registration Complete</Typography>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(loginPath)}
              fullWidth
            >
              Go to Sign In
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 56, color: theme.palette.error.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Registration Issue</Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(`${loginPath}?mode=signup`)}
              fullWidth
              sx={{ mb: 1 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(loginPath)}
              fullWidth
            >
              Go to Sign In
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};
